import React, { useState } from 'react';
import Button from 'react/components/button';
import Checkbox from 'react/components/checkbox';
import useGym from 'helpers/use_gym';
import useMobileAppSettings from 'helpers/use_mobile_app_settings';
import Dropdown from 'react/components/dropdown';
import Input from 'react/components/input';
import Label from 'react/components/label';
import RadioButtons from 'react/components/radio_buttons';
import { GymMembershipDetail } from 'react/types/gym';
import { useAngularService } from 'helpers/get_angular_service';
import DateInputPicker from 'react/components/date_input_picker';
import { useResponsive } from 'helpers/use_responsive';
import ErrorMessage from 'react/components/error_message';
import plural from 'helpers/plural';

export const multiplierLabelMap: Record<string, string> = {
  schedule_cancel_fee_multiplier_before: 'before',
  schedule_cancel_fee_multiplier_after: 'after',
  schedule_cancel_fee_multiplier_on: 'on',
};

export enum CancellationPolicyFormEnum {
  MEMBERSHIP_PLAN = 'membership_plan',
  MEMBERSHIP = 'membership',
}

type CancellationPolicyFormProps = {
  cancellationPolicyConfig: GymMembershipDetail;
  extendCancellationPolicyConfig: (detail: any) => void;
  type?: CancellationPolicyFormEnum;
  errors?: Record<string, string[]>;
};

const CancellationPolicyForm = (props: CancellationPolicyFormProps) => {
  const { cancellationPolicyConfig, extendCancellationPolicyConfig, type, errors } = props;
  const [showNoticePeriodDatePicker, setShowNoticePeriodDatePicker] = useState(
    cancellationPolicyConfig.mobile_app_cancellation_notice_period_number > 0,
  );

  const [showMinMembershipLengthDatePicker, setShowMinMembershipLengthDatePicker] = useState(
    cancellationPolicyConfig.mobile_app_cancellation_minimum_term_number > 0,
  );

  const [showCancellationFeeInput, setShowCancellationFeeInput] = useState(
    cancellationPolicyConfig.fee_for_mobile_app_cancellations > 0,
  );

  const gym = useGym();
  const mobileAppSettings = useMobileAppSettings();

  const interruptionRequestsAvailable =
    gym.mobile_app_enabled &&
    mobileAppSettings != null &&
    (mobileAppSettings.custom_app ||
      !mobileAppSettings.features_disabled?.includes('cancellation_requests'));
  const ReactModal = useAngularService('ReactModal');
  const { isMobile } = useResponsive();

  const manageCancellationReasons = () => {
    ReactModal.open({ component: 'ManageMembershipCancellationReasonsModal' });
  };

  const buildCancellationSummaryItems = () => {
    const paymentSummaryItems: React.ReactNode[] = [];

    {
      interruptionRequestsAvailable &&
        paymentSummaryItems.push(
          cancellationPolicyConfig.allow_cancellation_requests_from_mobile_app ? (
            cancellationPolicyConfig.mobile_app_cancellation_requests_require_review ? (
              <>
                <strong>Can be requested by members</strong> through the mobile app, but will
                <strong> require approval</strong> in Clubworx before being processed.
              </>
            ) : (
              <>
                <strong>Can be requested by members</strong> through the mobile app, and will be
                <strong> approved automatically without review.</strong>
              </>
            )
          ) : (
            <>
              <strong>May not be requested by members</strong> from the mobile app.
            </>
          ),
        );
    }
    if (
      cancellationPolicyConfig.allow_cancellation_requests_from_mobile_app &&
      cancellationPolicyConfig.allow_cancellation_requests_from_mobile_app_outside_of_policy
    ) {
      paymentSummaryItems.push(
        cancellationPolicyConfig.mobile_app_cancellation_requests_require_review ? (
          <>
            <strong>Will be allowed </strong>from the mobile app, even if the request is outside the
            bounds of the cancellation policy — however these requests
            <strong> will require review/approval</strong>.
          </>
        ) : (
          <>
            <strong>Will be allowed </strong>from the mobile app, even if the request is outside the
            bounds of the cancellation policy.
          </>
        ),
      );
    }

    const notice = cancellationPolicyConfig.mobile_app_cancellation_notice_period_number;
    const minTerm = cancellationPolicyConfig.mobile_app_cancellation_minimum_term_number;
    if (notice > 0 && minTerm > 0) {
      paymentSummaryItems.push(
        <>
          Require{' '}
          <strong>
            {notice}{' '}
            {plural(cancellationPolicyConfig.mobile_app_cancellation_notice_period_unit, notice)}{' '}
            notice
          </strong>{' '}
          and{' '}
          <strong>
            {minTerm}{' '}
            {plural(cancellationPolicyConfig.mobile_app_cancellation_minimum_term_unit, minTerm)} of
            the membership{' '}
          </strong>{' '}
          to have elapsed prior to the cancellation date.
        </>,
      );
    } else if (notice > 0) {
      paymentSummaryItems.push(
        <>
          Require{' '}
          <strong>
            {notice}{' '}
            {plural(cancellationPolicyConfig.mobile_app_cancellation_notice_period_unit, notice)}
          </strong>{' '}
          notice before the cancellation date.
        </>,
      );
    } else if (minTerm > 0) {
      paymentSummaryItems.push(
        <>
          Require{' '}
          <strong>
            {minTerm}{' '}
            {plural(cancellationPolicyConfig.mobile_app_cancellation_minimum_term_unit, minTerm)} of
            the membership
          </strong>{' '}
          to have elapsed prior to the cancellation date.
        </>,
      );
    } else {
      paymentSummaryItems.push(<>Can be processed at any time.</>);
    }

    if (cancellationPolicyConfig.fee_for_mobile_app_cancellations > 0) {
      if (
        cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_strategy ===
        'bundle_cancel_fee_with_final_payment_or_on_cancel_date'
      ) {
        paymentSummaryItems.push(
          <>
            Will incur a{' '}
            <strong>
              {gym.currency_symbol}
              {Number(cancellationPolicyConfig.fee_for_mobile_app_cancellations || 0).toFixed(
                2,
              )}{' '}
              cancellation fee,
            </strong>{' '}
            charged <strong>with the final membership payment</strong> (or on the cancellation date,
            if there is no final payment).
          </>,
        );
      } else {
        const rawFee = cancellationPolicyConfig.fee_for_mobile_app_cancellations;
        const numericFee = Number(rawFee) || 0;
        const amt = numericFee.toFixed(2);
        const mult = cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_multiplier;
        const num = cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_number;
        const unit = cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_unit;
        const anchorMap: Record<string, string> = {
          schedule_cancel_fee_relative_to_request_approval: 'cancellation approved',
          schedule_cancel_fee_relative_to_cancellation_date: 'cancellation date',
          schedule_cancel_fee_relative_to_final_payment: 'final payment date',
        };
        const anchor =
          anchorMap[cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_anchor] || '';

        const multLabel = multiplierLabelMap[mult] || '';

        paymentSummaryItems.push(
          <>
            Will incur a <strong>{gym.currency_symbol + amt} cancellation fee</strong>, charged{' '}
            {mult !== 'schedule_cancel_fee_multiplier_on' ? (
              <>
                <>
                  <strong>
                    {num} {plural(unit, num)}
                  </strong>{' '}
                  {multLabel} <strong>{anchor}</strong>.
                </>
              </>
            ) : (
              <>
                on <strong>{anchor}</strong>.
              </>
            )}
          </>,
        );
      }
    } else {
      paymentSummaryItems.push(
        <>
          Incurs <strong>no cancellation fee.</strong>
        </>,
      );
    }

    return paymentSummaryItems.filter(Boolean);
  };

  return (
    <>
      <>
        <div className="content-box-mini first-one">
          {type === CancellationPolicyFormEnum.MEMBERSHIP && (
            <h4 className="light first-one">
              Membership cancellation defaults{' '}
              <Button
                variant="link"
                onClick={manageCancellationReasons}
                className={isMobile ? 'margin-top-4' : 'margin-left-8'}
              >
                Manage cancellation reasons
              </Button>
            </h4>
          )}
          {interruptionRequestsAvailable && (
            <div className="form-group">
              <Checkbox
                checked={cancellationPolicyConfig.allow_cancellation_requests_from_mobile_app}
                onChange={(val) => {
                  extendCancellationPolicyConfig({
                    allow_cancellation_requests_from_mobile_app: val,
                  });
                  if (!val) {
                    extendCancellationPolicyConfig({
                      mobile_app_cancellation_requests_require_review: false,
                      allow_cancellation_requests_from_mobile_app_outside_of_policy: false,
                    });
                  }
                }}
              >
                Allow members to request cancellation on the mobile app{' '}
                {type === CancellationPolicyFormEnum.MEMBERSHIP_PLAN && (
                  <Button
                    variant="link"
                    onClick={manageCancellationReasons}
                    className="margin-top-4"
                  >
                    Manage cancellation reasons
                  </Button>
                )}
              </Checkbox>
              {errors?.allow_cancellation_requests_from_mobile_app && (
                <ErrorMessage error={errors.allow_cancellation_requests_from_mobile_app[0]} />
              )}
              <div className="helper-text cancellation-policy-text">
                A Cancel option will appear on the membership plan on the member mobile app. If not
                selected, membership cancellations can only be performed by Clubworx users.
              </div>
            </div>
          )}
          {interruptionRequestsAvailable &&
            cancellationPolicyConfig.allow_cancellation_requests_from_mobile_app && (
              <>
                <div className="form-group margin-left-24">
                  <Checkbox
                    checked={
                      cancellationPolicyConfig.mobile_app_cancellation_requests_require_review
                    }
                    onChange={(val) =>
                      extendCancellationPolicyConfig({
                        mobile_app_cancellation_requests_require_review: val,
                      })
                    }
                  >
                    Require approval
                  </Checkbox>
                  {errors?.mobile_app_cancellation_requests_require_review && (
                    <ErrorMessage
                      error={errors.mobile_app_cancellation_requests_require_review[0]}
                    />
                  )}
                </div>
                <div className="form-group margin-left-24">
                  <Checkbox
                    checked={
                      cancellationPolicyConfig.allow_cancellation_requests_from_mobile_app_outside_of_policy
                    }
                    onChange={(val) =>
                      extendCancellationPolicyConfig({
                        allow_cancellation_requests_from_mobile_app_outside_of_policy: val,
                      })
                    }
                  >
                    Allow requests if membership falls outside cancellation policy
                  </Checkbox>
                  {errors?.allow_cancellation_requests_from_mobile_app_outside_of_policy && (
                    <ErrorMessage
                      error={
                        errors.allow_cancellation_requests_from_mobile_app_outside_of_policy[0]
                      }
                    />
                  )}
                </div>
              </>
            )}
          <div className="form-group">
            <Checkbox
              checked={showNoticePeriodDatePicker}
              onChange={(val) => {
                setShowNoticePeriodDatePicker(val);
                if (!val) {
                  extendCancellationPolicyConfig({
                    mobile_app_cancellation_notice_period_number: 0,
                    mobile_app_cancellation_notice_period_unit: null,
                  });
                } else {
                  extendCancellationPolicyConfig({
                    mobile_app_cancellation_notice_period_number: 1,
                    mobile_app_cancellation_notice_period_unit: 'weeks',
                  });
                }
              }}
            >
              Set a notice period
            </Checkbox>
          </div>
          {showNoticePeriodDatePicker && (
            <div className="margin-bottom-16 margin-left-24">
              <DateInputPicker
                value={cancellationPolicyConfig.mobile_app_cancellation_notice_period_number || 1}
                selectedUnit={
                  cancellationPolicyConfig.mobile_app_cancellation_notice_period_unit || 'weeks'
                }
                options={[
                  { value: 'days', label: 'day(s)' },
                  { value: 'weeks', label: 'week(s)' },
                  { value: 'months', label: 'month(s)' },
                ]}
                onValueChange={(num) =>
                  extendCancellationPolicyConfig({
                    mobile_app_cancellation_notice_period_number: num,
                  })
                }
                onUnitChange={(unit) =>
                  extendCancellationPolicyConfig({
                    mobile_app_cancellation_notice_period_unit: unit,
                  })
                }
              />
              {errors?.mobile_app_cancellation_notice_period_number && (
                <ErrorMessage error={errors.mobile_app_cancellation_notice_period_number[0]} />
              )}
              <span className="margin-left-4"> after request</span>
            </div>
          )}
          <div className="form-group">
            <Checkbox
              checked={showMinMembershipLengthDatePicker}
              onChange={(val) => {
                setShowMinMembershipLengthDatePicker(val);
                if (!val) {
                  extendCancellationPolicyConfig({
                    mobile_app_cancellation_minimum_term_number: 0,
                    mobile_app_cancellation_minimum_term_unit: null,
                  });
                } else {
                  extendCancellationPolicyConfig({
                    mobile_app_cancellation_minimum_term_number: 1,
                    mobile_app_cancellation_minimum_term_unit: 'weeks',
                  });
                }
              }}
            >
              Set a minimum membership length
            </Checkbox>
          </div>
          {showMinMembershipLengthDatePicker && (
            <div className="margin-bottom-16 margin-left-24">
              <DateInputPicker
                value={cancellationPolicyConfig.mobile_app_cancellation_minimum_term_number || 1}
                selectedUnit={
                  cancellationPolicyConfig.mobile_app_cancellation_minimum_term_unit || 'weeks'
                }
                options={[
                  { value: 'days', label: 'day(s)' },
                  { value: 'weeks', label: 'week(s)' },
                  { value: 'months', label: 'month(s)' },
                ]}
                onValueChange={(num: number) =>
                  extendCancellationPolicyConfig({
                    mobile_app_cancellation_minimum_term_number: num,
                  })
                }
                onUnitChange={(unit: string) =>
                  extendCancellationPolicyConfig({
                    mobile_app_cancellation_minimum_term_unit: unit,
                  })
                }
              />
              {errors?.mobile_app_cancellation_minimum_term_number && (
                <ErrorMessage error={errors.mobile_app_cancellation_minimum_term_number[0]} />
              )}
              <span className="margin-left-8">after membership plan starts</span>
            </div>
          )}
          <div className="form-group">
            <Checkbox
              checked={showCancellationFeeInput}
              onChange={(val) => {
                setShowCancellationFeeInput(val);
                if (!val) {
                  extendCancellationPolicyConfig({
                    fee_for_mobile_app_cancellations: 0,
                    mobile_app_cancel_fee_default_due_date_strategy: null,
                    mobile_app_cancel_fee_default_due_date_number: 0,
                    mobile_app_cancel_fee_default_due_date_unit: null,
                    mobile_app_cancel_fee_default_due_date_multiplier: null,
                    mobile_app_cancel_fee_default_due_date_anchor: null,
                  });
                } else {
                  extendCancellationPolicyConfig({
                    mobile_app_cancel_fee_default_due_date_strategy:
                      'bundle_cancel_fee_with_final_payment_or_on_cancel_date',
                  });
                }
              }}
            >
              Charge a cancellation fee
            </Checkbox>
          </div>
          {showCancellationFeeInput && (
            <>
              <div className="form-inline cancellation-policy-input margin-bottom-16 margin-left-24">
                <div className="input-group group-with-addon">
                  <span className="input-group-addon">{gym.currency_symbol}</span>
                  <Input
                    type="number"
                    size="sm"
                    value={cancellationPolicyConfig.fee_for_mobile_app_cancellations}
                    onChange={(fee_for_mobile_app_cancellations) =>
                      extendCancellationPolicyConfig({ fee_for_mobile_app_cancellations })
                    }
                  />
                </div>
                {errors?.fee_for_mobile_app_cancellations && (
                  <ErrorMessage error={errors.fee_for_mobile_app_cancellations[0]} />
                )}
              </div>
              <div className="form-group margin-left-24">
                <Label>Schedule the cancellation fee to be charged:</Label>
                <RadioButtons
                  vertical
                  value={cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_strategy}
                  onChange={(strategy) => {
                    extendCancellationPolicyConfig({
                      mobile_app_cancel_fee_default_due_date_strategy: strategy,
                    });
                    if (strategy === 'use_custom_rule_for_cancel_fee_due_date') {
                      extendCancellationPolicyConfig({
                        mobile_app_cancel_fee_default_due_date_number: 0,
                        mobile_app_cancel_fee_default_due_date_unit: 'days',
                        mobile_app_cancel_fee_default_due_date_multiplier:
                          'schedule_cancel_fee_multiplier_on',
                        mobile_app_cancel_fee_default_due_date_anchor:
                          'schedule_cancel_fee_relative_to_cancellation_date',
                      });
                    }
                  }}
                  options={[
                    {
                      label: 'Bundle with final payment or on cancel date',
                      value: 'bundle_cancel_fee_with_final_payment_or_on_cancel_date',
                    },
                    {
                      label: 'Use custom rule for due date',
                      value: 'use_custom_rule_for_cancel_fee_due_date',
                    },
                  ]}
                  getLabel={(o) => o.label}
                  getValue={(o) => o.value}
                />
                {cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_strategy ===
                  'use_custom_rule_for_cancel_fee_due_date' && (
                  <>
                    <div className="cancellation-policy-form-inline margin-top-8">
                      <DateInputPicker
                        value={
                          cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_number ??
                          0
                        }
                        selectedUnit={
                          cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_unit ||
                          'days'
                        }
                        options={[
                          { value: 'days', label: 'day(s)' },
                          { value: 'weeks', label: 'week(s)' },
                        ]}
                        onValueChange={(num: any) =>
                          extendCancellationPolicyConfig({
                            mobile_app_cancel_fee_default_due_date_number: parseInt(num, 10),
                          })
                        }
                        onUnitChange={(unit: string) =>
                          extendCancellationPolicyConfig({
                            mobile_app_cancel_fee_default_due_date_unit: unit,
                          })
                        }
                        disabled={
                          cancellationPolicyConfig?.mobile_app_cancel_fee_default_due_date_multiplier ===
                          'schedule_cancel_fee_multiplier_on'
                        }
                      />

                      <Dropdown
                        hideEmptyOption
                        value={
                          cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_multiplier
                        }
                        options={[
                          {
                            value: 'schedule_cancel_fee_multiplier_after',
                            label: 'after',
                          },
                          {
                            value: 'schedule_cancel_fee_multiplier_before',
                            label: 'before',
                          },
                          {
                            value: 'schedule_cancel_fee_multiplier_on',
                            label: 'on',
                          },
                        ].filter((opt) => {
                          const anchor =
                            cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_anchor;
                          if (anchor === 'schedule_cancel_fee_relative_to_request_approval') {
                            return opt.value !== 'schedule_cancel_fee_multiplier_before';
                          }
                          return true;
                        })}
                        onSelect={(multiplier: string) => {
                          if (multiplier === 'schedule_cancel_fee_multiplier_on') {
                            extendCancellationPolicyConfig({
                              mobile_app_cancel_fee_default_due_date_multiplier: multiplier,
                              mobile_app_cancel_fee_default_due_date_number: 0,
                              mobile_app_cancel_fee_default_due_date_unit: 'days',
                            });
                          } else {
                            extendCancellationPolicyConfig({
                              mobile_app_cancel_fee_default_due_date_multiplier: multiplier,
                            });
                          }
                        }}
                      />

                      <Dropdown
                        value={
                          cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_anchor
                        }
                        hideEmptyOption
                        options={[
                          {
                            value: 'schedule_cancel_fee_relative_to_request_approval',
                            label: 'cancellation approved',
                          },
                          {
                            value: 'schedule_cancel_fee_relative_to_cancellation_date',
                            label: 'cancellation date',
                          },
                          {
                            value: 'schedule_cancel_fee_relative_to_final_payment',
                            label: 'final payment date',
                          },
                        ]}
                        onSelect={(anchor: string) => {
                          let multiplier =
                            cancellationPolicyConfig.mobile_app_cancel_fee_default_due_date_multiplier;

                          if (
                            anchor === 'schedule_cancel_fee_relative_to_request_approval' &&
                            multiplier === 'schedule_cancel_fee_multiplier_before'
                          ) {
                            multiplier = 'schedule_cancel_fee_multiplier_on';
                          }

                          extendCancellationPolicyConfig({
                            mobile_app_cancel_fee_default_due_date_anchor: anchor,
                            mobile_app_cancel_fee_default_due_date_multiplier: multiplier,
                            ...(multiplier === 'schedule_cancel_fee_multiplier_on'
                              ? {
                                  mobile_app_cancel_fee_default_due_date_number: 0,
                                  mobile_app_cancel_fee_default_due_date_unit: 'days',
                                }
                              : {}),
                          });
                        }}
                      />
                    </div>
                    <ErrorMessage error={errors?.mobile_app_cancel_fee_default_due_date_number} />
                  </>
                )}
              </div>
            </>
          )}
          <div className="form-group">
            <Label>For any payments due before cancellation date</Label>
            <RadioButtons
              vertical
              value={cancellationPolicyConfig.mobile_app_cancellation_payments_policy}
              onChange={(policy) =>
                extendCancellationPolicyConfig({
                  mobile_app_cancellation_payments_policy: policy,
                })
              }
              options={[
                {
                  label: 'Delete all payments between approval and cancellation date',
                  value: 'cancellation_payments_policy_delete_all_payments',
                },
                {
                  label: 'Let payments continue until cancellation date',
                  value: 'cancellation_payments_policy_delete_payments_after_cancellation',
                },
              ]}
              getLabel={(o) => o.label}
              getValue={(o) => o.value}
            />
          </div>
        </div>
      </>

      {type === CancellationPolicyFormEnum.MEMBERSHIP_PLAN && (
        <div className="content-box-mini margin-top-16">
          <div className="fields-group-addition">
            <div className="margin-bottom-16">Cancellation of this membership plan:</div>
            <ul className="cancellation-policy-summary">
              {buildCancellationSummaryItems().map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default CancellationPolicyForm;
